import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { createBrand, updateBrand } from "../services/catalogService";
import Toggle from "./Toggle";
import TranslatableField from "./TranslatableField";

const EMPTY = { name: { en: "", ar: "" }, isActive: true, logo: null };

/** Create or edit one brand. `brand` null means create. */
export default function BrandModal({ brand, open, onClose, onSaved }) {
  const { t } = useTranslation();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [saving, setSaving] = useState(false);

  const editing = Boolean(brand);

  useEffect(() => {
    if (!open) return;

    setValues(
      brand
        ? {
            name: {
              en: brand.translations?.name?.en ?? "",
              ar: brand.translations?.name?.ar ?? "",
            },
            isActive: Boolean(brand.is_active),
            logo: null,
          }
        : EMPTY
    );
    setErrors({});
    setBanner("");
  }, [open, brand]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, saving]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBanner("");

    const clientErrors = {};
    if (!values.name.en.trim()) clientErrors["name.en"] = [t("products.form.errors.nameEn")];
    if (!values.name.ar.trim()) clientErrors["name.ar"] = [t("products.form.errors.nameAr")];

    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    setErrors({});
    setSaving(true);

    try {
      const saved = editing ? await updateBrand(brand.id, values) : await createBrand(values);
      onSaved(saved, editing);
      onClose();
    } catch (err) {
      setErrors(apiFieldErrors(err));
      setBanner(apiErrorMessage(err, t("brands.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div role="dialog" aria-modal="true" className="w-full max-w-lg rounded-xl bg-surface shadow-xl">
        <form onSubmit={submit} noValidate>
          <div className="flex items-center justify-between border-b border-line px-5 py-3">
            <h2 className="text-sm font-semibold">
              {editing ? t("brands.editTitle") : t("brands.createTitle")}
            </h2>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              aria-label={t("common.close")}
              className="rounded p-1 text-ink-muted hover:bg-raised hover:text-ink-2"
            >
              ✕
            </button>
          </div>

          <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
            {banner && (
              <div className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
                {banner}
              </div>
            )}

            <fieldset disabled={saving} className="space-y-4">
              <TranslatableField
                name="name"
                label={t("brands.name")}
                value={values.name}
                onChange={(name) => setValues((v) => ({ ...v, name }))}
                errors={errors}
                required
              />

              <div>
                <label className="field-label" htmlFor="brand-logo">
                  {t("brands.logo")}
                </label>
                <input
                  id="brand-logo"
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  className="field-input"
                  onChange={(e) => setValues((v) => ({ ...v, logo: e.target.files[0] ?? null }))}
                />
                <p className="mt-1 text-xs text-ink-2">{t("brands.logoHint")}</p>
                {errors.logo?.[0] && <p className="field-error">{errors.logo[0]}</p>}
                {editing && brand.logo && !values.logo && (
                  <img src={brand.logo} alt="" className="mt-2 h-12 w-12 rounded-lg object-cover" />
                )}
              </div>

              <div className="rounded-lg border border-line p-3">
                <Toggle
                  id="brand-active"
                  checked={values.isActive}
                  onChange={(checked) => setValues((v) => ({ ...v, isActive: checked }))}
                  label={t("products.form.active")}
                  error={errors.is_active?.[0]}
                />
              </div>
            </fieldset>
          </div>

          <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
            <button type="button" onClick={onClose} disabled={saving} className="btn-secondary">
              {t("common.cancel")}
            </button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? t("products.form.saving") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
